import { getTokenStageRole } from '../base/jwt/functions';
import { toState } from '../base/redux/functions';
/**
 * Source marker used by the backend/Prosody for chat messages and poll activity that originated
 * from the visitor (viewer) node rather than the main conference room.
 */
export const VISITOR_SOURCE = 'visitor';
/**
 * Source marker used for regular main-room chat messages and poll activity. This is also the value
 * we normalize missing/unknown source metadata to, so legacy payloads keep behaving as main-room.
 */
export const MAIN_SOURCE = 'main';
/**
 * Whether the given source string marks visitor-originated activity.
 *
 * NOTE: This classifies a message/poll's origin only. It must NOT be confused with {@link iAmVisitor},
 * which tells whether the local client is currently a visitor. A main user can legitimately receive
 * messages/votes whose source is "visitor".
 *
 * @param {string} [source] - The raw source value carried by the message/poll payload.
 * @returns {boolean} Whether the activity originated from a visitor.
 */
export function isVisitorSource(source) {
    return source === VISITOR_SOURCE;
}
/**
 * Normalizes an optional/unknown source value to a stable {@code 'main' | 'visitor'} value. Missing
 * or unrecognized source metadata is treated as legacy main-room activity.
 *
 * @param {string} [source] - The raw source value carried by the message/poll payload.
 * @returns {MessageSource} The normalized source.
 */
export function normalizeMessageSource(source) {
    return source === VISITOR_SOURCE ? VISITOR_SOURCE : MAIN_SOURCE;
}
/**
 * Normalizes the chat thread a message belongs to. A message belongs to the visitor chat thread
 * when it either originated from a visitor ({@code source === 'visitor'}) or was explicitly directed
 * to the visitor thread by a main-room participant ({@code channel === 'visitor'}). Everything else
 * belongs to the main-room chat thread.
 *
 * @param {string} [channel] - The raw channel marker carried by the message payload.
 * @param {string} [source] - The raw source value carried by the message payload.
 * @returns {MessageSource} The normalized chat thread the message belongs to.
 */
export function normalizeMessageChannel(channel, source) {
    return channel === VISITOR_SOURCE || source === VISITOR_SOURCE ? VISITOR_SOURCE : MAIN_SOURCE;
}
/**
 * A short string to represent the number of visitors.
 * Over 100 we show numbers like 0.2 K or 9.5 K.
 *
 * @param {number} visitorsCount - The number of visitors to shorten.
 *
 * @returns {string} Short string representing the number of visitors.
 */
export function getVisitorsShortText(visitorsCount) {
    return visitorsCount > 100 ? `${Math.round(visitorsCount / 100) / 10} K` : String(visitorsCount);
}
/**
 * Selector to return a list of promotion requests from visitors.
 *
 * @param {IReduxState} state - State object.
 * @returns {IPromotionRequest[]}
 */
export function getPromotionRequests(state) {
    return state['features/visitors'].promotionRequests;
}
/**
 * Whether the local visitor's raise hand is blocked individually or by the
 * room-wide policy. Server-confirmed only.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState}
 * function.
 * @returns {boolean}
 */
export function isLocalHandBlocked(stateful) {
    const state = toState(stateful);
    const { iAmHandBlocked, raiseHandDisabled } = state['features/visitors'];
    return iAmVisitor(state) && Boolean(iAmHandBlocked || raiseHandDisabled);
}
/**
 * Selector to return the server-reported roster of the visitors currently on
 * the room's visitor nodes. Only ever populated for moderators.
 *
 * @param {IReduxState} state - State object.
 * @returns {IVisitor[]}
 */
export function getVisitorsRoster(state) {
    return state['features/visitors'].roster;
}
/**
 * Whether current UI is in visitor mode.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState}
 * function.
 * @returns {boolean} Whether iAmVisitor is set.
 */
export function iAmVisitor(stateful) {
    return toState(stateful)['features/visitors'].iAmVisitor;
}
/**
 * The loading screen for a move between the main meeting and viewer mode,
 * or undefined when no move is in progress.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState}.
 * @returns {IRoleTransition|undefined}
 */
export function getRoleTransition(stateful) {
    return toState(stateful)['features/visitors'].roleTransition;
}
const PROMOTION_ROLES = ['moderator', 'panelist', 'participant'];
/**
 * A role name a promotion may grant. Anything else (including a missing
 * value) is dropped so the loading screen never invents a role.
 *
 * @param {string} [role] - The raw role from the promotion response.
 * @returns {PromotionTargetRole|undefined}
 */
export function normalizePromotionRole(role) {
    if (role && PROMOTION_ROLES.indexOf(role) !== -1) {
        return role;
    }
    return undefined;
}
const ROLE_LABEL_KEY = {
    moderator: 'webinar.role.moderator',
    panelist: 'webinar.role.panelist',
    participant: 'webinar.role.participant',
    presenter: 'webinar.role.presenter'
};
/**
 * The sentence on the loading screen for a role move.
 *
 * @param {IRoleTransition|undefined} transition - The move in progress.
 * @param {Function} t - The i18n translate function.
 * @returns {string}
 */
export function getRoleTransitionText(transition, t) {
    if (!transition) {
        return '';
    }
    if (transition.kind === 'to-viewer') {
        return t('visitors.transition.toViewer');
    }
    const labelKey = transition.role && ROLE_LABEL_KEY[transition.role];
    if (!labelKey) {
        return t('visitors.transition.toMeeting');
    }
    return t('visitors.transition.toMeetingAs', {
        role: t(labelKey)
    });
}
/**
 * Whether the local user, joining the room described by {@code conferenceInfo}
 * now, would be sent to a visitor node: the room has visitor mode on and the
 * token (if any) claims no stage role. For the pre-join screen, so a guest is
 * told before they join rather than discovering it inside.
 *
 * Informational: jicofo makes the actual decision from the room's allow-list
 * when it answers the conference request. This mirrors the rule that fills
 * that list (see getTokenStageRole).
 *
 * @param {IStateful} stateful - The redux store or state.
 * @param {Object} conferenceInfo - The latest conference-info for the room.
 * @returns {boolean}
 */
export function willJoinAsVisitor(stateful, conferenceInfo) {
    if ((conferenceInfo === null || conferenceInfo === void 0 ? void 0 : conferenceInfo.visitorsEnabled) !== true) {
        return false;
    }
    return getTokenStageRole(toState(stateful)) === undefined;
}
/**
 * Returns the number of visitors.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState}
 * function.
 * @returns {number} - The number of visitors.
 */
export function getVisitorsCount(stateful) {
    var _a;
    return (_a = toState(stateful)['features/visitors'].count) !== null && _a !== void 0 ? _a : 0;
}
/**
 * Whether the visitor-count label should be hidden from the local viewer.
 * The webinar-safe default is true; deployments may explicitly set
 * {@code config.visitors.hideVisitorCountForVisitors = false} to expose it.
 *
 * @param {Function|Object} stateful - The redux store or state.
 * @returns {boolean} Whether the visitor-count label should be hidden.
 */
export function shouldHideVisitorsCount(stateful) {
    var _a, _b;
    const state = toState(stateful);
    const hideForVisitors = (_b = (_a = state['features/base/config'].visitors) === null || _a === void 0 ? void 0 : _a.hideVisitorCountForVisitors) !== null && _b !== void 0 ? _b : true;
    return Boolean(hideForVisitors && iAmVisitor(state));
}
/**
 * Whether the conference backend reports support for the visitors feature.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState} function.
 * @returns {boolean} Whether visitors are supported by the current conference.
 */
export function isVisitorModeSupported(stateful) {
    return toState(stateful)['features/visitors'].supported;
}
/**
 * Whether visitor mode is currently armed for the room. This reflects the actual armed state from
 * backend room metadata only - it defaults to OFF and is NOT inferred from the "supported" disco flag.
 *
 * Important: {@code supported} (conference.isVisitorsSupported()) only means the visitors component is
 * available in the deployment (e.g. ENABLE_VISITORS=1); it does NOT mean visitor mode is on for this
 * room. Deriving "enabled" from it made the toggle read ON even when nothing armed visitor mode.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState} function.
 * @returns {boolean} Whether visitor mode is enabled for the room.
 */
export function isVisitorsModeEnabled(stateful) {
    return Boolean(toState(stateful)['features/visitors'].enabled);
}
/**
 * Returns the active visitor toggle mode (e.g. 'new-guests-only' or 'move-current-guests').
 *
 * @param {Function|Object} stateful - The redux store or {@code getState} function.
 * @returns {string|undefined} The active visitor toggle mode.
 */
export function getVisitorsMode(stateful) {
    return toState(stateful)['features/visitors'].mode;
}
/**
 * Whether a moderator visitor-mode toggle request is currently in flight.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState} function.
 * @returns {boolean} Whether a toggle request is pending.
 */
export function isVisitorModeTogglePending(stateful) {
    return Boolean(toState(stateful)['features/visitors'].togglePending);
}
/**
 * Returns the latest moderator visitor-mode toggle error.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState} function.
 * @returns {string|undefined} The i18n key for the latest toggle error.
 */
export function getVisitorModeToggleError(stateful) {
    return toState(stateful)['features/visitors'].toggleError;
}
/**
 * Returns the value the visitor-mode switch should display: the requested value while a toggle is
 * pending, otherwise the confirmed room state. This keeps the switch responsive without optimistically
 * deciding the room mode in {@code enabled}.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState} function.
 * @returns {boolean} The value the visitor-mode switch should show.
 */
export function getVisitorsToggleValue(stateful) {
    const state = toState(stateful)['features/visitors'];
    if (state.togglePending && typeof state.requestedValue === 'boolean') {
        return state.requestedValue;
    }
    return isVisitorsModeEnabled(stateful);
}
/**
 * Whether a moderator auto-promote toggle request is in flight.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState} function.
 * @returns {boolean}
 */
export function isAutoPromoteTogglePending(stateful) {
    return Boolean(toState(stateful)['features/visitors'].autoPromotePending);
}
/**
 * Returns the latest moderator auto-promote toggle error.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState} function.
 * @returns {string|undefined} The i18n key for the latest toggle error.
 */
export function getAutoPromoteToggleError(stateful) {
    return toState(stateful)['features/visitors'].autoPromoteError;
}
/**
 * Returns the value the auto-promote switch should display: the requested value
 * while a toggle is pending, otherwise the confirmed room metadata state. When
 * the room has no explicit setting the switch shows off; the deployment default
 * (AUTO_ALLOW_VISITOR_PROMOTION) still applies server-side and is OR-ed with the
 * room setting there.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState} function.
 * @returns {boolean}
 */
export function getAutoPromoteToggleValue(stateful) {
    const state = toState(stateful)['features/visitors'];
    if (state.autoPromotePending && typeof state.autoPromoteRequestedValue === 'boolean') {
        return state.autoPromoteRequestedValue;
    }
    return Boolean(state.autoPromote);
}
/**
 * Whether a moderator raise-hand block toggle request is in flight.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState} function.
 * @returns {boolean}
 */
export function isRaiseHandDisabledTogglePending(stateful) {
    return Boolean(toState(stateful)['features/visitors'].raiseHandDisabledPending);
}
/**
 * Returns the latest moderator raise-hand block toggle error.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState} function.
 * @returns {string|undefined} The i18n key for the latest toggle error.
 */
export function getRaiseHandDisabledToggleError(stateful) {
    return toState(stateful)['features/visitors'].raiseHandDisabledError;
}
/**
 * Returns the value the raise-hand block switch should display: the requested
 * value while a toggle is pending, otherwise the confirmed room metadata state.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState} function.
 * @returns {boolean}
 */
export function getRaiseHandDisabledToggleValue(stateful) {
    const state = toState(stateful)['features/visitors'];
    if (state.raiseHandDisabledPending && typeof state.raiseHandDisabledRequestedValue === 'boolean') {
        return state.raiseHandDisabledRequestedValue;
    }
    return Boolean(state.raiseHandDisabled);
}
